import { createContext, useState, useEffect, ReactNode } from "react";
import { authAPI } from "@/lib/api/auth";

interface User {
  phoneNumber: string;
  name?: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  checkDevice: (phoneNumber: string) => Promise<boolean>;
  sendCode: (phoneNumber: string) => Promise<string>;
  verifyCode: (codeId: string, code: string, name?: string) => Promise<void>;
  logout: () => void;
}

// eslint-disable-next-line react-refresh/only-export-components
export const AuthContext = createContext<AuthContextType | undefined>(
  undefined,
);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Check if user is already authenticated
    const token = localStorage.getItem("auth_token");
    const phoneNumber = localStorage.getItem("user_phone");

    if (token && phoneNumber) {
      setUser({ phoneNumber });
    }
    setIsLoading(false);
  }, []);

  const checkDevice = async (phoneNumber: string): Promise<boolean> => {
    try {
      const response = await authAPI.checkDevice(phoneNumber);

      if (response.authenticated && response.token) {
        localStorage.setItem("auth_token", response.token);
        localStorage.setItem("user_phone", phoneNumber);
        setUser({ phoneNumber });
        return true;
      }

      return false;
    } catch {
      return false;
    }
  };

  const sendCode = async (phoneNumber: string): Promise<string> => {
    const response = await authAPI.sendCode(phoneNumber);
    return response.codeId;
  };

  const verifyCode = async (
    codeId: string,
    code: string,
    name?: string,
  ): Promise<void> => {
    const response = await authAPI.verifyCode(codeId, code, name);
    localStorage.setItem("auth_token", response.token);
    localStorage.setItem("user_phone", response.phoneNumber);
    setUser({ phoneNumber: response.phoneNumber, name });
  };

  const logout = () => {
    localStorage.removeItem("auth_token");
    localStorage.removeItem("user_phone");
    setUser(null);
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    checkDevice,
    sendCode,
    verifyCode,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
